import { useRef, useCallback, useReducer } from 'react';
import { useIsMounted } from './useIsMounted';

export enum AsyncStatus {
  Idle = 'idle',
  Pending = 'pending',
  Success = 'success',
  Failure = 'failure',
}

export type AsyncFunction = (...args: any[]) => Promise<any>;

/*
  Given a type with the signature () => Promise<T>, this type matches T. Otherwise, the type is considered unknown.
*/
type PromiseResult<TAsyncFunc extends AsyncFunction> = TAsyncFunc extends (...args: any) => Promise<infer U>
  ? U
  : unknown;

export interface AsyncStateData<TResult, TError = Error> {
  status: AsyncStatus;
  value?: TResult;
  error?: TError;
}
export interface AsyncState<TExecFunc extends AsyncFunction, TError = Error>
  extends AsyncStateData<PromiseResult<TExecFunc>, TError> {
  execute: (...args: Parameters<TExecFunc>) => Promise<void>;
  reset: () => void;
}

export interface AsyncStateDispatchAction<TResult, TError> {
  type: 'start' | 'complete' | 'fail' | 'reset';
  value?: TResult;
  error?: TError;
}

type DispatcherState<TResult, TError> = AsyncStateData<TResult, TError>;
export function asyncStateDispatcher<TResult, TError = Error>(
  state: DispatcherState<TResult, TError>,
  action: AsyncStateDispatchAction<TResult, TError>
): DispatcherState<TResult, TError> {
  const pendingState = {
    status: AsyncStatus.Pending,
  };

  switch (action.type) {
    case 'start':
      return state.status === AsyncStatus.Success ? state : pendingState;
    case 'reset':
      return {
        status: AsyncStatus.Idle,
      };
    case 'complete':
      return {
        status: AsyncStatus.Success,
        value: action.value,
      };
    case 'fail':
      return {
        status: AsyncStatus.Failure,
        error: action.error,
      };
    default:
      // eslint-disable-next-line no-console
      console.warn(`Attempted unknown action on useAsync state [${action.type as string}]`);
      return state;
  }
}

export const useAsync = <TExecFunc extends AsyncFunction, TError = Error>(
  fn: TExecFunc
): AsyncState<TExecFunc, TError> => {
  const [stateData, dispatch] = useReducer(asyncStateDispatcher, {
    status: AsyncStatus.Idle,
  });
  const { value, error, status } = stateData as AsyncStateData<PromiseResult<TExecFunc>, TError>;
  const isMounted = useIsMounted();

  // Store the status as a reference so we can use within the callback without creating new versions
  // of the execute function when the status changes.
  const statusRef = useRef(status);
  statusRef.current = status;

  const execute = useCallback(
    async (...args: Parameters<TExecFunc>) => {
      if (statusRef.current === AsyncStatus.Pending) {
        return;
      }

      dispatch({
        type: 'start',
      });
      try {
        const result = (await fn(...args)) as PromiseResult<TExecFunc>;
        if (isMounted()) {
          dispatch({ type: 'complete', value: result });
        }
      } catch (err) {
        if (isMounted()) {
          dispatch({ type: 'fail', error: err as TError });
        }
      }
    },
    [fn, dispatch, isMounted]
  );

  const reset = useCallback(() => {
    dispatch({ type: 'reset' });
  }, [dispatch]);

  return { value, error, status, execute, reset };
};
